import React, { useState } from "react";
import { Calendar, ArrowRight, ChevronDown } from "lucide-react";
import { BlogPost } from "../types";
import { ProductImage } from "./ProductCard";

interface BlogSectionProps {
  posts: BlogPost[];
  onPostClick: (post: BlogPost) => void;
}

const BlogSection: React.FC<BlogSectionProps> = ({ posts, onPostClick }) => {
  const [visibleCount, setVisibleCount] = useState(3);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Sort posts by date (newest first)
  const sortedPosts = [...posts].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const visiblePosts = sortedPosts.slice(0, visibleCount);

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    // Simulate loading delay for better UX
    setTimeout(() => {
      setVisibleCount((prev) => prev + 3);
      setIsLoadingMore(false);
    }, 600);
  };

  return (
    <div
      id="blog-section"
      className="bg-white py-16 px-6 border-t border-slate-100"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-10">
          <h2 className="text-2xl font-bold text-slate-900 uppercase tracking-tight">
            Latest Insights
          </h2>
          <div className="h-px bg-slate-200 flex-1"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {visiblePosts.map((post) => (
            <div
              key={post.id}
              className="group cursor-pointer flex flex-col h-full bg-white border border-slate-100 rounded-xl overflow-hidden hover:shadow-xl hover:border-slate-300 transition-all duration-300"
              onClick={() => onPostClick(post)}
            >
              <div className="h-48 overflow-hidden bg-slate-100 relative">
                <ProductImage
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 text-xs font-bold text-slate-700 rounded-full shadow-sm">
                  Blog
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                  <Calendar size={12} /> {post.date}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug group-hover:text-orange-600 transition-colors">
                  {post.title}
                </h3>
                <p
                  className="text-slate-500 text-sm line-clamp-3 mb-6 flex-1"
                  dangerouslySetInnerHTML={{
                    __html:
                      post.excerpt ||
                      (post.content &&
                        post.content.substring(0, 100) + "...") ||
                      "",
                  }}
                />
                <div className="flex items-center text-sm font-bold text-slate-900 mt-auto group-hover:translate-x-2 transition-transform">
                  Read Article{" "}
                  <ArrowRight size={16} className="ml-2 text-orange-500" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {visibleCount < sortedPosts.length && (
          <div className="mt-12 text-center">
            <button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="inline-flex items-center gap-2 px-8 py-3 bg-white border border-slate-300 text-slate-700 font-bold rounded-full hover:bg-slate-50 hover:border-slate-400 transition-all shadow-sm text-sm uppercase tracking-wide disabled:opacity-70 disabled:cursor-wait"
            >
              {isLoadingMore ? (
                <>
                  <div className="w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full animate-spin"></div>
                  Loading...
                </>
              ) : (
                <>
                  Load More Articles <ChevronDown size={16} />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogSection;
